import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Submenu, type SubmenuItem } from '../Submenu/Submenu';
import designTokens from '@/design-tokens';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import DashboardIcon from '../Icons/Dashboard';
import TagOneIcon from '../Icons/TagOne';
import ShopIcon from '../Icons/Shop';
import ApplicationOneIcon from '../Icons/ApplicationOne';
import StirIcon from '../Icons/Stir';
import PeoplesIcon from '../Icons/Peoples';
import SettingTwoIcon from '../Icons/SettingTwo';
import ChefHatIcon from '../Icons/ChefHat';
import DeliveryIcon from '../Icons/Delivery';
import { Product as ProductIcon } from '../Icons/Product';
import StaffIcon from '../Icons/Staff';
import TrialCountdownIcon from '../Icons/TrialCountdown';
import { Truck as TruckIcon } from '../Icons/Truck';
import { useTenant, type Tenant } from '@/contexts/TenantContext';
import { Avatar } from '@/components/ui/User/Avatar';
import { usePermissions } from '@/hooks/usePermissions';
import { useResponsive } from '@/contexts/ResponsiveContext';

export interface LeftNavigationBarProps {
  className?: string;
  currentPath?: string;
  isDrawerOpen?: boolean;
  onCloseDrawer?: () => void;
}

interface NavigationSubItemProps {
  label: string;
  selected?: boolean;
  disabled?: boolean;
  hasNew?: boolean;
  onClick?: () => void;
}

const NavigationSubItem: React.FC<NavigationSubItemProps> = ({
  label,
  selected = false,
  disabled = false,
  hasNew = false,
  onClick
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      className="w-full flex items-center justify-between transition-all duration-200"
      style={{
        height: '24px',
        padding: '0 6px',
        backgroundColor: selected
          ? isHovered ? (designTokens.surface?.brand?.['primary-contrast'] || '#1b8e56') : (designTokens.surface?.brand?.primary || '#2bb673')
          : isHovered ? (designTokens.surface?.brand?.['secondary-contrast'] || '#7dddb5') : 'transparent',
        color: selected
          ? (designTokens.text?.inverse || '#ffffff')
          : disabled
          ? (designTokens['surface/brand/secondary-contrast'] || '#7dddb5')
          : (designTokens.text?.primary || '#111827'),
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: designTokens.font?.family?.primary || 'Montserrat, system-ui, sans-serif',
        fontWeight: designTokens.font?.weight?.regular || '400',
        fontSize: designTokens.font?.size?.xs || '0.75rem',
        lineHeight: '1rem',
        border: 'none',
        outline: 'none',
        borderRadius: '0.5rem', // radius/md
      }}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
    >
      <span className="text-left" style={{ whiteSpace: 'nowrap' }}>{label}</span>
      {hasNew && (
        <span className="px-1.5 py-0.5 bg-green-500 text-white text-xs rounded">New</span>
      )}
    </button>
  );
};

interface NavigationItemProps {
  icon: React.ReactNode;
  label: string;
  selected?: boolean;
  disabled?: boolean;
  hasNew?: boolean;
  hasSubItems?: boolean;
  subItems?: Array<{
    label: string;
    selected?: boolean;
    disabled?: boolean;
    hasNew?: boolean;
  }>;
  isExpanded?: boolean;
  navExpanded?: boolean;
  onClick?: () => void;
  onExpand?: () => void;
  onNavigate?: (itemId: string, hasSubItems?: boolean) => void;
}

const NavigationItem: React.FC<NavigationItemProps> = ({
  icon,
  label,
  selected = false,
  disabled = false,
  hasNew = false,
  hasSubItems = false,
  subItems = [],
  isExpanded = false,
  navExpanded = false,
  onClick,
  onExpand,
  onNavigate
}) => {
  const { isCompact } = useResponsive();
  const [isHovered, setIsHovered] = useState(false);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [submenuTop, setSubmenuTop] = useState(0);
  const submenuRef = useRef<HTMLDivElement>(null);
  
  // Calculate estimated submenu height for consistent use
  const getEstimatedSubmenuHeight = () => {
    // SubmenuOption: 24px height (compact menu items)
    // Gap between items: 16px (spacing/sixteen) 
    // Container padding: 48px total (24px top + 24px bottom = spacing/twenty-four)
    const estimatedItemHeight = 24; // Compact submenu item height
    const itemGap = 16; // designTokens['spacing/sixteen'] = 1rem = 16px
    const containerPadding = 48; // designTokens['spacing/twenty-four'] = 1.5rem = 24px * 2
    return (subItems.length * estimatedItemHeight) + ((subItems.length - 1) * itemGap) + containerPadding;
  };

  // Calculate estimated submenu position based on number of items
  const calculateCenteredPosition = () => {
    if (itemRef.current) {
      const navItemRect = itemRef.current.getBoundingClientRect();
      const navItemHeight = 32;
      const navItemCenter = navItemRect.top + navItemHeight / 2;
      
      const estimatedSubmenuHeight = getEstimatedSubmenuHeight();
      const centeredTop = navItemCenter - estimatedSubmenuHeight / 2;
      
      setSubmenuTop(centeredTop);
    }
  };
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const itemRef = useRef<HTMLDivElement>(null);

  // Check if any sub-item is selected
  const hasSelectedSubItem = subItems.some(item => item.selected);
  const isParentOrChildSelected = selected || hasSelectedSubItem;

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (hasSubItems && navExpanded && !isExpanded && !isCompact) {
      hoverTimeoutRef.current = setTimeout(() => {
        calculateCenteredPosition();
        setShowSubmenu(true);
      }, 300);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Start hide timer
    hideTimeoutRef.current = setTimeout(() => {
      setShowSubmenu(false);
    }, 300);
  };

  // Function to keep submenu visible (called by bridge and submenu)
  const keepSubmenuVisible = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // Function to start hide timer (called by bridge and submenu on leave)
  const startHideTimer = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowSubmenu(false);
    }, 200);
  };

  const handleClick = () => {
    if (hasSubItems) {
      if (onExpand) onExpand();
      // In compact mode, only expand — don't navigate to first child
      if (!isCompact && onClick) onClick();
    } else {
      if (onClick) onClick();
    }
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const iconColor = isParentOrChildSelected
    ? (designTokens['icon/color/brand-hover'] || '#1b8e56')
    : disabled
    ? (designTokens['icon/color/disabled'] || '#9ca3af')
    : (designTokens['icon/color/primary'] || '#343a40');

  const textColor = isParentOrChildSelected
    ? (designTokens['text/brand-hover'] || '#1b8e56')
    : disabled
    ? (designTokens['text/disabled'] || '#9ca3af')
    : (designTokens['text/primary'] || '#111827');

  const bgColor = isParentOrChildSelected
    ? (isHovered && !isExpanded) ? (designTokens.surface?.brand?.['secondary-contrast'] || '#7dddb5') : (designTokens.surface?.brand?.secondary || '#d5f9ee')
    : (isHovered && !isExpanded)
    ? (designTokens.surface?.greyscale?.mist || '#f3f4f6')
    : 'transparent';

  // Convert subItems to SubmenuItem format
  const submenuItems: SubmenuItem[] = subItems.map((item) => ({
    id: item.label.toLowerCase().replace(/\s+/g, '-'),
    label: item.label,
    value: item.label.toLowerCase().replace(/\s+/g, '-'),
    disabled: item.disabled,
  }));

  const handleSubmenuSelect = (item: SubmenuItem) => {
    // Map submenu labels to route keys
    const labelToRoute: { [key: string]: string } = {
      'Sales Orders': 'sales-orders',
      'Transport Orders': 'transport-orders',
      'Make Orders': 'make-orders',
      'Purchase Orders': 'purchase-orders',
      'Suppliers': 'suppliers',
      'Marketplace': 'marketplace',
      'Ingredients': 'ingredients',
      'Materials': 'materials',
      'Staff': 'staff',
      'Recipes': 'recipes',
      'Packages': 'packages',
      'Products': 'products',
      'Customers': 'customers'
    };

    const routeKey = labelToRoute[item.label];
    if (routeKey && onNavigate) {
      onNavigate(routeKey, false);
    }
    setShowSubmenu(false);
  };

  return (
    <div>
      <div ref={itemRef} className="relative">
        <button
          className="w-full flex items-center justify-between transition-all duration-200 relative"
          style={{
            height: designTokens['nav/item/height'] || '2rem',
            padding: '0 6px',
            backgroundColor: bgColor,
            cursor: disabled ? 'default' : 'pointer',
            border: 'none',
            outline: 'none',
            borderRadius: isExpanded && hasSubItems && navExpanded && isParentOrChildSelected
              ? '0.5rem 0.5rem 0 0' // Top corners only when expanded with selected children
              : '0.5rem', // All corners normally
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={!disabled ? handleClick : undefined}
          disabled={disabled}
        >
          <div className="flex items-center" style={{ gap: '8px' }}>
            <div className="flex-shrink-0 nav-icon-container" style={{ color: iconColor, width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {icon}
            </div>
            {navExpanded && (
              <span
                className="truncate transition-opacity duration-[400ms]"
                style={{
                  color: textColor,
                  fontFamily: designTokens.font?.family?.primary || 'Montserrat, system-ui, sans-serif',
                  fontWeight: designTokens.font?.weight?.medium || '500',
                  fontSize: designTokens.font?.size?.sm || '0.875rem',
                  lineHeight: '1.25rem', // font/line-height/sm (20px)
                  opacity: navExpanded ? 1 : 0,
                  transitionTimingFunction: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            )}
          </div>
          {navExpanded && (
            <>
              {hasNew && !hasSubItems && (
                <span className="px-1.5 py-0.5 bg-green-500 text-white text-xs rounded">New</span>
              )}
              {hasSubItems && (
                <div 
                  style={{ 
                    color: iconColor,
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 300ms ease-out',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ChevronRight size={20} />
                </div>
              )}
            </>
          )}
        </button>

        {/* Hover bridge - invisible area to prevent submenu from disappearing */}
        {showSubmenu && hasSubItems && !isExpanded && navExpanded && (
          <div
            className="fixed"
            style={{
              left: designTokens['nav/width/expanded'] || '13.75rem',
              top: `${submenuTop - 10}px`, // Start slightly above submenu
              width: '20px', // Bridge gap
              height: `${getEstimatedSubmenuHeight() + 20}px`, // Match submenu height + buffer
              zIndex: 9998, // Below submenu but above other content
            }}
            onMouseEnter={keepSubmenuVisible}
            onMouseLeave={startHideTimer}
          />
        )}

        {/* Hover submenu using Submenu component */}
        {showSubmenu && hasSubItems && !isExpanded && navExpanded && (
          <div
            ref={submenuRef}
            className="fixed"
            style={{
              left: `calc(${designTokens['nav/width/expanded'] || '13.75rem'} + 0.5rem)`,
              top: `${submenuTop}px`,
              zIndex: 9999,
            }}
            onMouseEnter={keepSubmenuVisible}
            onMouseLeave={startHideTimer}
          >
            <Submenu
              items={submenuItems}
              onSelect={handleSubmenuSelect}
              variant="dropdown"
              showCheckmarks={false}
              showIcons={false}
              isOpen={true}
              width="auto"
              noTruncate={false}
            />
          </div>
        )}
      </div>

      {/* Expanded subitems */}
      <div 
        className="overflow-hidden"
        style={{
          maxHeight: isExpanded && hasSubItems && navExpanded ? '500px' : '0',
          opacity: isExpanded && hasSubItems && navExpanded ? 1 : 0,
          transition: 'max-height 300ms ease-out, opacity 300ms ease-out',
          backgroundColor: isParentOrChildSelected ? (designTokens.surface?.brand?.secondary || '#d5f9ee') : (designTokens.surface?.greyscale?.clear || '#f9fafb'),
          borderRadius: '0 0 0.5rem 0.5rem',
          marginTop: isExpanded && hasSubItems && navExpanded ? '-1px' : '0',
        }}
      >
        <div
          style={{
            padding: '4px 6px 6px 34px',
          }}
        >
          <div className="flex flex-col" style={{ gap: '4px' }}>
            {subItems.map((item, index) => {
              // Map sub item labels to route keys
              const labelToRoute: { [key: string]: string } = {
                'Sales Orders': 'sales-orders',
                'Transport Orders': 'transport-orders',
                'Make Orders': 'make-orders',
                'Purchase Orders': 'purchase-orders',
                'Suppliers': 'suppliers',
                'Marketplace': 'marketplace',
                'Ingredients': 'ingredients',
                'Materials': 'materials',
                'Staff': 'staff',
                'Recipes': 'recipes',
                'Packages': 'packages',
                'Products': 'products',
                'Customers': 'customers'
              };
              
              const routeKey = labelToRoute[item.label];
              
              return (
                <NavigationSubItem
                  key={index}
                  label={item.label}
                  selected={item.selected}
                  disabled={item.disabled}
                  hasNew={item.hasNew}
                  onClick={() => routeKey && onNavigate && onNavigate(routeKey, false)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

interface NavigationalPanelDividerProps {
  title: string;
  navExpanded?: boolean;
}

const NavigationalPanelDivider: React.FC<NavigationalPanelDividerProps> = ({
  title,
  navExpanded = false
}) => {
  return (
    <div 
      className="flex items-center"
      style={{
        height: '24px',
        padding: '0px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {!navExpanded ? (
        // Line divider - shown when collapsed
        <div
          className="transition-all duration-300"
          style={{
            width: '100%',
            height: '1px',
            backgroundColor: designTokens.border?.color?.default || '#ced4da',
          }}
        />
      ) : (
        // Text title - shown when expanded
        <div
          className="transition-opacity duration-300"
          style={{
            color: designTokens.text?.secondary || '#6b7280',
            fontFamily: designTokens.font?.family?.primary || 'Montserrat, system-ui, sans-serif',
            fontWeight: designTokens.font?.weight?.semibold || '600',
            fontSize: designTokens.font?.size?.xxs || '0.625rem',
            lineHeight: '0.875rem',
            whiteSpace: 'nowrap'
          }}
        >
          {title}
        </div>
      )}
    </div>
  );
};

const CompanyLogo: React.FC<{ expanded: boolean; tenant: Tenant | null }> = ({ expanded, tenant }) => {
  const companyName = tenant?.name || '';

  return (
    <div
      className="flex items-center"
      style={{
        height: '32px',
        width: '100%',
        overflow: 'hidden',
        gap: '12px',
      }}
    >
      <Avatar
        variant="company"
        companyName={companyName}
        imageUrl={tenant?.logo_url || undefined}
        alt={companyName}
        size="medium"
      />
      <div
        className="transition-opacity duration-[400ms]"
        style={{
          opacity: expanded ? 1 : 0,
          width: expanded ? '156px' : '0px',
          flexShrink: 0,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          fontFamily: designTokens['font/family/primary'] || designTokens.font?.family?.primary || 'Montserrat, system-ui, sans-serif',
          fontWeight: designTokens['font/weight/semibold'] || designTokens.font?.weight?.semibold || '600',
          fontSize: '0.875rem',
          color: designTokens.text?.primary || '#212529',
          lineHeight: '1.3',
          wordBreak: 'break-word',
        }}
      >
        {companyName}
      </div>
    </div>
  );
};

const UpgradeNavButton: React.FC<{
  daysRemaining: number;
  totalDays?: number;
  navExpanded: boolean;
  onClick: () => void;
}> = ({ daysRemaining, totalDays, navExpanded, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const bgColor = isHovered
    ? (designTokens.surface?.brand?.['primary-contrast'] || '#1b8e56')
    : (designTokens.surface?.brand?.primary || '#2bb673');

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '32px',
        padding: '0 6px',
        gap: '8px',
        borderRadius: '0.5rem',
        border: 'none',
        backgroundColor: bgColor,
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
    >
      <div className="flex-shrink-0 nav-icon-container" style={{ width: '20px', height: '20px' }}>
        <TrialCountdownIcon daysRemaining={daysRemaining} totalDays={totalDays} size={20} />
      </div>
      {navExpanded && (
        <span
          className="truncate"
          style={{
            fontFamily: designTokens.font?.family?.primary || 'Montserrat, system-ui, sans-serif',
            fontWeight: designTokens.font?.weight?.medium || '500',
            fontSize: designTokens.font?.size?.sm || '0.875rem',
            lineHeight: '1.25rem',
            whiteSpace: 'nowrap',
          }}
        >
          Upgrade Now
        </span>
      )}
    </button>
  );
};

export const LeftNavigationBar: React.FC<LeftNavigationBarProps> = ({
  className = '',
  currentPath = '',
  isDrawerOpen = false,
  onCloseDrawer,
}) => {
  const navigate = useNavigate();
  const { isCompact, isMobile } = useResponsive();
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { showIngredients, showRecipes, showMaterials, showPackages, showMakeOrders } = useFeatureVisibility();
  const { tenant } = useTenant();
  const { canViewFinancials, canAccessPurchaseOrders, canAccessFulfillment, canAccessSalesOrders, canAccessContacts, canAccessStaff, canAccessSettings, canAccessBilling } = usePermissions();

  // Reset hover expansion when entering compact/mobile mode
  useEffect(() => {
    if (isCompact) {
      setIsHoverExpanded(false);
    }
  }, [isCompact]);

  // In compact mode, the nav is always "expanded" (showing labels) when the drawer is open.
  // On desktop, expansion is controlled by hover.
  const isExpanded = isCompact ? isDrawerOpen : isHoverExpanded;

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (isCompact && isDrawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isCompact, isDrawerOpen]);

  // Trial countdown — "now" captured once at mount; the memo only recomputed on
  // tenant change anyway, so this preserves behavior while keeping render pure
  const [mountedAtMs] = useState(() => Date.now());
  const trialDaysRemaining = useMemo(() => {
    if (!tenant || tenant.subscription_status !== 'trial' || !tenant.trial_ends_at) return null;
    const msRemaining = new Date(tenant.trial_ends_at).getTime() - mountedAtMs;
    const days = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
    return days > 0 ? days : null;
  }, [tenant, mountedAtMs]);

  // Filter sub-items based on feature visibility
  const resourcesSubItems = useMemo(() => {
    const items: Array<{ label: string; selected?: boolean }> = [];
    if (showIngredients) items.push({ label: 'Ingredients' });
    if (showMaterials) items.push({ label: 'Materials' });
    if (canAccessStaff) items.push({ label: 'Staff' });
    return items;
  }, [showIngredients, showMaterials, canAccessStaff]);

  const productionSubItems = useMemo(() => {
    const items: Array<{ label: string; selected?: boolean }> = [];
    if (showRecipes) items.push({ label: 'Recipes' });
    if (showPackages) items.push({ label: 'Packages' });
    items.push({ label: 'Products' }); // Always visible
    return items;
  }, [showRecipes, showPackages]);
  
  // Determine selected item based on current path (RESTful routing)
  const getSelectedItem = () => {
    const path = currentPath.toLowerCase();
    
    // Handle RESTful routes - check for specific paths first
    if (path.startsWith('/ingredients')) return 'ingredients';
    if (path.startsWith('/materials')) return 'materials';
    if (path.startsWith('/staff')) return 'staff';
    if (path.startsWith('/recipes')) return 'recipes';
    if (path.startsWith('/packages')) return 'packages';
    if (path.startsWith('/products')) return 'products';
    if (path.startsWith('/sales-orders')) return 'sales-orders';
    if (path.startsWith('/transport-orders')) return 'transport-orders';
    if (path.startsWith('/make-orders')) return 'make-orders';
    if (path.startsWith('/purchase-orders')) return 'purchase-orders';
    if (path.startsWith('/marketplace')) return 'marketplace';
    if (path.startsWith('/suppliers')) return 'suppliers';
    if (path.startsWith('/customers')) return 'customers';
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/dashboard') || path === '/') return 'dashboard';
    
    return 'dashboard'; // fallback
  };
  
  const [selectedItem, setSelectedItem] = useState<string>(getSelectedItem());
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Auto-expand parent if child is selected (exclusive expansion)
  useEffect(() => {
    const selected = getSelectedItem();
    setSelectedItem(selected);

    // Map child items to their parent groups
    const childToParent = {
      'purchase-orders': 'purchasing',
      'marketplace': 'purchasing',
      'customers': 'contacts',
      'suppliers': 'contacts',
      'ingredients': 'resources',
      'materials': 'resources',
      'staff': 'resources',
      'recipes': 'assembly',
      'packages': 'assembly',
      'products': 'assembly'
    };

    const parentGroup = childToParent[selected as keyof typeof childToParent];
    if (parentGroup) {
      // Exclusively expand only the parent of the selected item
      setExpandedItems([parentGroup]);
    } else {
      // If no parent group, collapse all
      setExpandedItems([]);
    }
  }, [currentPath]);

  const handleMouseEnter = () => {
    if (isCompact) return; // Drawer mode — no hover behavior
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setIsHoverExpanded(true);
  };

  const handleMouseLeave = () => {
    if (isCompact) return; // Drawer mode — no hover behavior
    leaveTimeoutRef.current = setTimeout(() => {
      setIsHoverExpanded(false);
    }, 150);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  const toggleExpanded = (itemId: string) => {
    if (isCompact) {
      // Compact/mobile: tap to expand/collapse any parent group
      setExpandedItems(prev => {
        if (prev.includes(itemId)) {
          return prev.filter(id => id !== itemId);
        } else {
          return [itemId]; // Only this item expanded
        }
      });
      return;
    }

    // Desktop: only allow toggling the currently active parent group
    const selected = getSelectedItem();
    const childToParent = {
      'purchase-orders': 'purchasing',
      'marketplace': 'purchasing',
      'customers': 'contacts',
      'suppliers': 'contacts',
      'ingredients': 'resources',
      'materials': 'resources',
      'staff': 'resources',
      'recipes': 'assembly',
      'packages': 'assembly',
      'products': 'assembly'
    };

    const currentParentGroup = childToParent[selected as keyof typeof childToParent];

    if (currentParentGroup === itemId) {
      setExpandedItems(prev => {
        if (prev.includes(itemId)) {
          return prev.filter(id => id !== itemId);
        } else {
          return [itemId]; // Only this item expanded
        }
      });
    }
  };

  const handleItemClick = (itemId: string, hasSubItems?: boolean) => {
    // Map of parent groups with their children
    const parentGroups: { [key: string]: string[] } = {
      'purchasing': ['purchase-orders', 'marketplace'],
      'contacts': ['customers', 'suppliers'],
      'resources': ['ingredients', 'materials', 'staff'],
      'assembly': ['recipes', 'packages', 'products']
    };

    // Navigate to appropriate routes
    const routeMap: { [key: string]: string } = {
      'dashboard': '/dashboard',
      'ingredients': '/ingredients',
      'materials': '/materials',
      'staff': '/staff',
      'recipes': '/recipes',
      'packages': '/packages',
      'products': '/products',
      'sales-orders': '/sales-orders',
      'transport-orders': '/transport-orders',
      'make-orders': '/make-orders',
      'purchase-orders': '/purchase-orders',
      'marketplace': '/marketplace',
      'suppliers': '/suppliers',
      'customers': '/customers',
      'settings': '/settings',
      'settings/profile': '/settings/profile'
    };

    const route = routeMap[itemId];
    if (route) {
      navigate(route);

      // If the item has subitems (parent group), keep menu open and expand it
      if (hasSubItems) {
        // Find which parent group this itemId belongs to
        let parentGroupKey: string | undefined;
        for (const [groupKey, children] of Object.entries(parentGroups)) {
          if (children.includes(itemId)) {
            parentGroupKey = groupKey;
            break;
          }
        }

        // Expand the parent group if found
        if (parentGroupKey) {
          setExpandedItems([parentGroupKey]);
        }

        // In compact mode, close the drawer after navigating (even for parent items)
        if (isCompact && onCloseDrawer) {
          onCloseDrawer();
        }
      } else {
        // For items without subitems, collapse the navigation
        if (isCompact && onCloseDrawer) {
          onCloseDrawer();
        } else {
          setIsHoverExpanded(false);
        }
      }
    }
  };

  // Drawer width: full screen on small phones (<440px), 280px otherwise
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 440);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 439px)');
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener('change', handler);
    setIsNarrow(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const drawerWidth = isNarrow ? '100vw' : '280px';

  return (
    <>
      {/* Backdrop overlay — compact mode only */}
      {isCompact && isDrawerOpen && (
        <div
          onClick={onCloseDrawer}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 39,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            transition: 'opacity 200ms ease',
          }}
        />
      )}
    <nav
      className={`fixed left-0 bg-white border-r z-40 ${className}`}
      style={{
        top: '0',
        height: '100dvh',
        width: isCompact
          ? drawerWidth
          : isExpanded
            ? (designTokens['nav/width/expanded'] || '13.75rem')
            : (designTokens['nav/width/collapsed'] || '4rem'),
        backgroundColor: designTokens.surface?.greyscale?.clear || '#ffffff',
        borderRightWidth: designTokens.border?.weight?.light || '0.0625rem',
        borderRightColor: designTokens.border?.color?.default || '#dee2e6',
        borderRightStyle: 'solid',
        // Slide in/out on compact, width transition on desktop
        transform: isCompact
          ? (isDrawerOpen ? 'translateX(0)' : 'translateX(-100%)')
          : undefined,
        transition: isCompact
          ? 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)'
          : 'width 400ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        overflow: 'visible',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="h-full flex flex-col" style={{ padding: `${designTokens['nav/padding/y'] || '1rem'} ${designTokens['nav/padding/x'] || '0.75rem'}`, overflow: 'visible' }}>
        {/* Company Logo */}
        <CompanyLogo expanded={isExpanded} tenant={tenant} />
        
        <div style={{ height: '24px' }} />

        {/* Main Navigation Content */}
        <div className="flex-1 overflow-y-auto overflow-x-visible">
          <div className="flex flex-col" style={{ gap: '6px' }}>
            {canViewFinancials && (
              <NavigationItem
                icon={<DashboardIcon />}
                label="Dashboard"
                selected={selectedItem === 'dashboard'}
                navExpanded={isExpanded}
                onClick={() => handleItemClick('dashboard')}
                onNavigate={handleItemClick}
              />
            )}

            {canAccessPurchaseOrders && (
              <NavigationalPanelDivider title="Operations" navExpanded={isExpanded} />
            )}

            {canAccessPurchaseOrders && (
              <NavigationItem
                icon={<ShopIcon />}
                label="Purchasing"
                hasSubItems
                subItems={[
                  { label: 'Purchase Orders', selected: selectedItem === 'purchase-orders' },
                  { label: 'Marketplace', selected: selectedItem === 'marketplace', disabled: true }
                ]}
                isExpanded={expandedItems.includes('purchasing')}
                navExpanded={isExpanded}
                onClick={() => handleItemClick('purchase-orders', true)}
                onExpand={() => toggleExpanded('purchasing')}
                onNavigate={handleItemClick}
              />
            )}

            {showMakeOrders && (
              <NavigationItem
                icon={<ChefHatIcon />}
                label="Make Orders"
                selected={selectedItem === 'make-orders'}
                navExpanded={isExpanded}
                onClick={() => handleItemClick('make-orders')}
                onNavigate={handleItemClick}
              />
            )}

            {canAccessSalesOrders && (
              <NavigationItem
                icon={<TagOneIcon />}
                label="Sales Orders"
                selected={selectedItem === 'sales-orders'}
                navExpanded={isExpanded}
                onClick={() => handleItemClick('sales-orders')}
                onNavigate={handleItemClick}
              />
            )}

            <NavigationItem
              icon={<TruckIcon color="currentColor" />}
              label="Transport Orders"
              selected={selectedItem === 'transport-orders'}
              navExpanded={isExpanded}
              onClick={() => handleItemClick('transport-orders')}
              onNavigate={handleItemClick}
            />

            <NavigationalPanelDivider title="Catalog" navExpanded={isExpanded} />

            {/* Co-packer only: Products above Staff. Otherwise: Resources above Production. */}
            {productionSubItems.length === 1 && productionSubItems[0]?.label === 'Products'
              && resourcesSubItems.length === 1 && resourcesSubItems[0]?.label === 'Staff' ? (
              <>
                <NavigationItem
                  icon={<ProductIcon color="currentColor" strokeWidth={1.5} />}
                  label="Products"
                  selected={selectedItem === 'products'}
                  navExpanded={isExpanded}
                  onClick={() => handleItemClick('products')}
                  onNavigate={handleItemClick}
                />
                <NavigationItem
                  icon={<StaffIcon />}
                  label="Staff"
                  selected={selectedItem === 'staff'}
                  navExpanded={isExpanded}
                  onClick={() => handleItemClick('staff')}
                  onNavigate={handleItemClick}
                />
              </>
            ) : (
              <>
                {resourcesSubItems.length === 1 && resourcesSubItems[0]?.label === 'Staff' ? (
                  <NavigationItem
                    icon={<StaffIcon />}
                    label="Staff"
                    selected={selectedItem === 'staff'}
                    navExpanded={isExpanded}
                    onClick={() => handleItemClick('staff')}
                    onNavigate={handleItemClick}
                  />
                ) : (
                  <NavigationItem
                    icon={<ApplicationOneIcon />}
                    label="Resources"
                    hasSubItems
                    subItems={resourcesSubItems.map(item => ({
                      ...item,
                      selected: selectedItem === item.label.toLowerCase()
                    }))}
                    isExpanded={expandedItems.includes('resources')}
                    navExpanded={isExpanded}
                    onClick={() => handleItemClick(resourcesSubItems[0]?.label.toLowerCase() || 'staff', true)}
                    onExpand={() => toggleExpanded('resources')}
                    onNavigate={handleItemClick}
                  />
                )}

                {productionSubItems.length === 1 && productionSubItems[0]?.label === 'Products' ? (
                  <NavigationItem
                    icon={<ProductIcon color="currentColor" strokeWidth={1.5} />}
                    label="Products"
                    selected={selectedItem === 'products'}
                    navExpanded={isExpanded}
                    onClick={() => handleItemClick('products')}
                    onNavigate={handleItemClick}
                  />
                ) : (
                  <NavigationItem
                    icon={<StirIcon />}
                    label="Production"
                    hasSubItems
                    subItems={productionSubItems.map(item => ({
                      ...item,
                      selected: selectedItem === item.label.toLowerCase()
                    }))}
                    isExpanded={expandedItems.includes('assembly')}
                    navExpanded={isExpanded}
                    onClick={() => handleItemClick(productionSubItems[0]?.label.toLowerCase() || 'products', true)}
                    onExpand={() => toggleExpanded('assembly')}
                    onNavigate={handleItemClick}
                  />
                )}
              </>
            )}

            {canAccessContacts && (
              <>
                <NavigationalPanelDivider title="Contacts" navExpanded={isExpanded} />

                <NavigationItem
                  icon={<PeoplesIcon />}
                  label="Customers"
                  selected={selectedItem === 'customers'}
                  navExpanded={isExpanded}
                  onClick={() => handleItemClick('customers')}
                  onNavigate={handleItemClick}
                />

                <NavigationItem
                  icon={<DeliveryIcon />}
                  label="Suppliers"
                  selected={selectedItem === 'suppliers'}
                  navExpanded={isExpanded}
                  onClick={() => handleItemClick('suppliers')}
                  onNavigate={handleItemClick}
                />
              </>
            )}

          </div>
        </div>

        {/* Footer Navigation */}
        <div className="overflow-y-auto overflow-x-visible">
          <div className="flex flex-col" style={{ gap: '6px' }}>
          {canAccessSettings ? (
            <NavigationItem
              icon={<SettingTwoIcon />}
              label="Settings"
              selected={selectedItem === 'settings'}
              navExpanded={isExpanded}
              onClick={() => handleItemClick('settings')}
              onNavigate={handleItemClick}
            />
          ) : (
            <NavigationItem
              icon={<SettingTwoIcon />}
              label="Profile"
              selected={selectedItem === 'settings'}
              navExpanded={isExpanded}
              onClick={() => handleItemClick('settings/profile')}
              onNavigate={handleItemClick}
            />
          )}

          {/* Upgrade button - visible during trial, owner only */}
          {trialDaysRemaining !== null && canAccessBilling && (
            <UpgradeNavButton
              daysRemaining={trialDaysRemaining}
              totalDays={tenant?.settings?.is_pilot ? 90 : 14}
              navExpanded={isExpanded}
              onClick={() => navigate('/settings/subscription')}
            />
          )}
          </div>
        </div>
      </div>
    </nav>
    </>
  );
};